import type { CalendarEventDto } from '@chat-emp/shared';

import { Avatar } from '../components/Avatar.js';
import { AvatarStack } from '../components/AvatarStack.js';
import { Icon } from '../components/Icon.js';
import { formatEventDay, formatTimeRange } from '../util/format.js';
import { isOver } from './event-rules.js';
import { facesOf, guestCount, replyTally } from './invitees.js';
import { readVenue } from './location.js';
import { Soon } from './Soon.js';
import { statusLabel } from './status.js';
import styles from './EventCard.module.css';

interface EventCardProps {
  event: CalendarEventDto;
  /** Whether the details panel is showing this one. */
  selected: boolean;
  /** Whether the band it sits in leaves the day for the row to state. */
  showDate: boolean;
  onOpen: () => void;
}

/**
 * One event, as the column beside the calendar lists it.
 *
 * Three lines, each answering one question rather than carrying whatever fits:
 * what it is, when and where, then who and where their answer got to. Read down
 * the left they are the order the questions get asked in; read across, nothing
 * on a line competes with anything on another.
 *
 * The right-hand edge answers whatever the band header did not. Under Today and
 * Tomorrow the day is settled, so it counts the hours ("In 2 hours", "Happening
 * now"); under Upcoming and Finished it names the date, because "In 9 days" is
 * not something anyone can write in a diary.
 *
 * The third line names one person or counts many. A Chat Day for twenty-four
 * has no name to print — showing one of them would say the wrong thing more
 * confidently than saying nothing — so it shows a few faces, says how many are
 * coming, and leaves who to the panel that has room for the list.
 *
 * Once answers start coming back the count is joined by a tally, because at
 * that point "24 guests" has stopped being the useful number: what the employer
 * is deciding is whether to chase the six who have not replied.
 */
export function EventCard({ event, selected, showDate, onOpen }: EventCardProps) {
  // Read once per render, not subscribed: the only thing here that follows the
  // clock is the relative label, and that is its own leaf (see Soon).
  const past = isOver(event);
  const timeRange = formatTimeRange(event.startsAt, event.endsAt);
  const venue = readVenue(event.location);
  const status = statusLabel(event.status);
  // A list of one is an ordinary one-to-one — see the DTO — so only a genuine
  // several changes what the third line says. Empty is possible and says
  // nothing: the line is left off rather than held open around a gap.
  const guests = event.invitees;
  const tally = replyTally(guests);

  return (
    <button
      type="button"
      className={[styles.card, past && styles.cardPast, selected && styles.cardOn]
        .filter(Boolean)
        .join(' ')}
      onClick={onOpen}
      aria-label={`${event.title}, ${timeRange}, ${venue.label}${status ? `, ${status}` : ''}`}
    >
      {/* The tile is the glyph for where it happens, as tall as all three lines,
          so a column of these reads as a column of *kinds* before it reads as
          words: a video call and a visit are two marks apart at a glance. */}
      <span className={styles.tile}>
        <Icon name={venue.icon} size={18} />
      </span>

      <span className={styles.body}>
        <span className={styles.head}>
          <span className={styles.title}>{event.title}</span>
          <span className={styles.when}>
            {showDate ? (
              formatEventDay(event.startsAt)
            ) : (
              <Soon startsAt={event.startsAt} endsAt={event.endsAt} />
            )}
          </span>
        </span>

        {/* When, then where. The hour in ink and the place muted after it: one is
            the fact the row is filed under, the other qualifies it. */}
        <span className={styles.meta}>
          <span className={styles.time}>{timeRange}</span>
          <span className={styles.dot} aria-hidden="true">
            ·
          </span>
          <span className={styles.where}>{venue.label}</span>
        </span>

        {/* Skipped entirely where nothing resolved: a row held open around an
            absent name is worse than three lines becoming two. */}
        {guests.length > 0 && (
          <span className={styles.people}>
            {guests.length > 1 ? (
              <>
                {/* Faces, not a glyph: at a glance a Chat Day should look like a
                    group before it reads as one. No `onMore` — this card is
                    itself a button, and the press already opens the panel that
                    holds the full list. */}
                <AvatarStack people={facesOf(guests)} max={3} size={20} />
                <span className={styles.count}>{guestCount(guests)}</span>
                {tally && (
                  <>
                    <span className={styles.dot} aria-hidden="true">
                      ·
                    </span>
                    <span className={styles.replies}>{tally}</span>
                  </>
                )}
              </>
            ) : (
              <>
                <Avatar name={guests[0]!.name} size={20} />
                <span className={styles.name}>{guests[0]!.name}</span>
              </>
            )}
            {/* Only where it says something — see statusLabel. */}
            {status && <span className={styles.status}>{status}</span>}
          </span>
        )}
      </span>
    </button>
  );
}
